import { createSelector } from 'reselect';

import { createKeySelector } from 'Lib/reduxUtils';
import isExpired from 'Lib/utils/isExpired';

import { getSelfieVerificationStatus } from 'Store/profile/selfieVerification.selectors';

import { SELFIE_VERIFICATION_UNVERIFIED } from 'Containers/ModalManager/modals/SelfieVerification/constants';

/**
 * Gets user namespace from the state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {UserState} entities by type
 */
export const selectUser = createKeySelector('user');

/**
 * Gets whether there is a user logged in
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} whether or not the user is logged in
 */
export const getIsUserLoggedIn = createSelector(selectUser, (user) =>
  __STATIC_SITE__ ? false : user.loggedIn ?? false
);

/**
 * Gets whether the user is currently logging out
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} is logging out
 */
export const getIsUserLoggingOut = createSelector(selectUser, (user) => user.loggingOut ?? false);

/**
 * Gets the verification code from the root state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} verification code
 */
export const getDeepLinkOtp = createSelector(selectUser, (user) => user.deeplinkOtp);

/**
 * Gets the  promo code from the root state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} promo code
 */
export const getIncentivePromoCode = createSelector(selectUser, (user) => user.promoCode);

/**
 * Gets the gift card number from the root state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} gift card number
 */
export const getGiftCardNumber = createSelector(selectUser, (user) => user.giftCardNumber ?? '');

/**
 * Gets the gift card pin from the root state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} gift card pin
 */
export const getGiftCardPin = createSelector(selectUser, (user) => user.giftCardPin ?? '');

/**
 * Gets the verification type from the root state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} verification code type
 */
export const getDeepLinkType = createSelector(selectUser, (user) => user.deeplinkType);

export const getIsGoldDeeplinkActive = createSelector(
  selectUser,
  (user) => user.isGoldDeeplinkActive ?? false
);

export const getIntroPriceDeeplinkTerm = createSelector(
  selectUser,
  (user) => user.introPriceTerm ?? 0
);

export const getIntroPriceDeeplinkDiscount = createSelector(
  selectUser,
  (user) => user.introPriceDiscount ?? 0
);

/**
 * Gets if we should navigate to the promo route from the root state
 *
 * @param  {GlobalStateType.Map} state - top-level state
 * @return {boolean} should show promo modal
 */
export const getShouldShowPromoPage = createSelector(selectUser, (user) => user.showPromo ?? false);

/**
 * Gets the captcha token used for Appealable bans that require captcha
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} appealChallengeToken
 */
export const getAppealChallengeToken = createSelector(
  selectUser,
  (user) => user.appealChallengeToken
);

/**
 * Get the arkose data blob required for captcha
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} arkoseDataBlob
 */
export const getArkoseDataBlob = createSelector(selectUser, (user) => user.arkoseDataBlob);

/**
 * Gets the navigation route from the root state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} deeplink navigation route
 */
export const getDeepLinkNavigationRoute = createSelector(
  selectUser,
  (user) => user.navigationRoute ?? ''
);

export const getAgeVerificationUrl = createSelector(selectUser, (user) =>
  isExpired(Math.floor(Date.now() / 1000), user.ageVerificationUrlTs)
    ? user.ageVerificationUrl
    : null
);

export const getAgeVerificationUrlError = createSelector(
  selectUser,
  (user) => user.ageVerificationUrlError
);

// Underage onboarding

export const getUnderageToken = createSelector(selectUser, (user) => user.underageToken);

export const getIsUnderageOnboarding = createSelector(
  selectUser,
  (user) => user.isUnderageOnboarding ?? false
);

// Request Verification

export const getSelfieVerificationAttempted = createSelector(
  selectUser,
  getSelfieVerificationStatus,
  (user, selfieVerificationStatus) =>
    selfieVerificationStatus === SELFIE_VERIFICATION_UNVERIFIED &&
    (user.selfieVerificationAttempted ?? false)
);

export const getIsUserLoggedInAsGuest = createSelector(selectUser, (user) => user?.loggedInGuest);

export const getPostLoginRedirectPath = createSelector(
  selectUser,
  (user) => user.postLoginRedirectPath
);

export const getPostLoginRedirectPushState = createSelector(
  selectUser,
  (user) => user.postLoginRedirectPushState
);
